import { z } from 'zod'
import type { Organization } from '#/domain/organization/Organization'
import type { InvalidOrganization } from '#/domain/organization/InvalidOrganization'
import type { OrganizationPage } from '#/domain/organization/OrganizationRepository'

export const OrganizationDtoSchema = z
  .object({
    Id: z.string().optional(),
    Name: z.string(),
    DisplayName: z.string(),
    Branding: z.object({
      LogoUrl: z.string().optional(),
      Colors: z
        .object({
          Primary: z.string().optional().default(''),
          PageBackground: z.string().optional().default(''),
        })
        .nullish()
        .transform((c) => c ?? { Primary: '', PageBackground: '' }),
    }),
    Metadata: z
      .object({
        Apps: z.string().optional().default(''),
        Language: z.string().optional(),
        Comments: z.string().optional(),
      })
      .nullish()
      .transform((m) => m ?? { Apps: '' }),
  })
  .transform((raw): Organization => ({
    Id: raw.Id,
    Name: raw.Name,
    DisplayName: raw.DisplayName,
    Branding: {
      LogoUrl: raw.Branding.LogoUrl,
      Colors: raw.Branding.Colors,
    },
    Metadata: raw.Metadata,
  }))

export type OrganizationResponseDto = z.input<typeof OrganizationDtoSchema>

export function toDomain(raw: OrganizationResponseDto): Organization {
  return OrganizationDtoSchema.parse(raw)
}

export function toDomainList(raw: OrganizationResponseDto[]): Organization[] {
  return raw.map(toDomain)
}

const emptyBranding: Organization['Branding'] = { Colors: { Primary: '', PageBackground: '' } }
const emptyMetadata: Organization['Metadata'] = { Apps: '' }

// Used for single-organization reads (detail view, edit dialog) where we need the real
// organization back — never the list-only InvalidOrganization placeholder. Falls back to
// empty Branding/Metadata instead of throwing when the payload doesn't validate, so legacy
// organizations can still be viewed/edited (and their missing fields filled in) rather than
// crashing the page.
export function toDomainFindById(raw: OrganizationResponseDto): Organization {
  const result = OrganizationDtoSchema.safeParse(raw)
  if (result.success) return result.data
  return {
    Id: raw.Id,
    Name: raw.Name,
    DisplayName: raw.DisplayName,
    Branding: emptyBranding,
    Metadata: emptyMetadata,
  }
}

// Auth0 organizations frequently don't satisfy the Organization contract
// (legacy tenants missing Branding). One bad record must not fail
// the whole list — surface it as an InvalidOrganization instead of throwing.
// This is a listing-only concept: editing/detail views use toDomainFindById instead,
// which returns the real organization rather than a flattened placeholder.
export function toDomainOrInvalid(raw: unknown): Organization | InvalidOrganization {
  const result = OrganizationDtoSchema.safeParse(raw)
  if (result.success) return result.data

  const safeRaw = (raw ?? {}) as Partial<OrganizationResponseDto>
  return {
    Id: safeRaw.Id,
    Name: safeRaw.Name ?? '',
    DisplayName: safeRaw.DisplayName ?? '',
    missingFields: [...new Set(result.error.issues.map((issue) => issue.path.join('.')))],
  }
}

const OrganizationPageDtoSchema = z.object({
  Items: z.array(z.unknown()),
  Total: z.number(),
})

export type OrganizationPageResponseDto = z.input<typeof OrganizationPageDtoSchema>

export function toDomainPage(raw: OrganizationPageResponseDto): OrganizationPage {
  const { Items, Total } = OrganizationPageDtoSchema.parse(raw)
  return {
    items: Items.map((item) => toDomainOrInvalid(item)),
    total: Total,
  }
}
